import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getAuthTokenFromCookie, getUserInfoFromCookie, clearAuthCookies } from '../../utils/cookies';

interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Get initial state from localStorage and cookies
const getInitialState = (): AuthState => {
  // Try localStorage first, then fallback to cookies
  let token = localStorage.getItem('token');
  let userStr = localStorage.getItem('user');
  
  // If no token in localStorage, check cookies
  if (!token) {
    token = getAuthTokenFromCookie();
    if (token) {
      // Sync to localStorage for consistency
      localStorage.setItem('token', token);
    }
  }
  
  // If no user in localStorage, check cookies
  if (!userStr) {
    const userFromCookie = getUserInfoFromCookie();
    if (userFromCookie) {
      userStr = JSON.stringify(userFromCookie);
      // Sync to localStorage for consistency
      localStorage.setItem('user', userStr);
    }
  }
  
  let user = null;
  if (userStr) {
    try {
      user = JSON.parse(userStr);
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }
  }

  return {
    user,
    token,
    isAuthenticated: !!(token && user),
    isLoading: false,
  };
};

const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialState(),
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      // Save to localStorage
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      // Save to localStorage
      localStorage.setItem('token', action.payload);
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Clear cookies
      clearAuthCookies();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
});

export const { setUser, setToken, logout, setLoading } = authSlice.actions;
export default authSlice.reducer; 